import { useEffect, useState, useRef } from "react";
import { Search, Plus, X, Pencil, Trash2, Upload, Video, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Loader, Pill } from "../components/common";
import VideoRecorder from "../components/VideoRecorder";

const CATS = ["All", "Strength", "Cardio", "Mobility", "Core", "Olympic", "Recovery"];
const blankForm = { name: "", category: "Strength", equipment: "Bodyweight", difficulty: "Beginner", description: "", muscleText: "", instructionsText: "", cuesText: "", mistakesText: "", safety_notes: "", video_url: null, thumbnail: null };

export default function Exercises() {
  const [exercises, setExercises] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [recordingVid, setRecordingVid] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const videoRef = useRef(null);
  const imageRef = useRef(null);

  const load = () => api.get(`/exercises${cat !== "All" ? `?category=${cat}` : ""}`).then(({ data }) => setExercises(data));
  useEffect(() => { load(); }, [cat]);
  if (!exercises) return <Loader />;

  const filtered = exercises.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));

  const openNew = () => { setEditId(null); setForm(blankForm); setShowNew(true); };
  const openEdit = (ex) => { setEditId(ex.id); setForm({ name: ex.name, category: ex.category, equipment: ex.equipment, difficulty: ex.difficulty, description: ex.description || "", muscleText: (ex.muscle_groups || []).join(", "), instructionsText: (ex.instructions || []).join("\n"), cuesText: (ex.cues || []).join("\n"), mistakesText: (ex.common_mistakes || []).join("\n"), safety_notes: ex.safety_notes || "", video_url: ex.video_url || null, thumbnail: ex.thumbnail || null }); setSelected(null); setShowNew(true); };

  const toBase64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

  const onVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Video too large (max 8MB)."); return; }
    try {
      const b64 = await toBase64(file);
      const { data } = await api.post("/upload/media", { data: b64, kind: "video" });
      setForm((f) => ({ ...f, video_url: data.url }));
      toast.success("Video attached.");
    } catch (err) { toast.error("Upload failed, please try again."); }
    e.target.value = "";
  };

  const onImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 2.5 * 1024 * 1024) { toast.error("Image too large (max 2.5MB)."); return; }
    try {
      const b64 = await toBase64(file);
      const { data } = await api.post("/upload/media", { data: b64, kind: "cover" });
      setForm((f) => ({ ...f, thumbnail: data.url }));
      toast.success("Cover image attached.");
    } catch (err) { toast.error("Upload failed, please try again."); }
    e.target.value = "";
  };

  const save = async () => {
    if (!form.name) return toast.error("Enter a name.");
    setSaving(true);
    try {
      const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
      const instructions = lines(form.instructionsText);
      const payload = {
        name: form.name, category: form.category, equipment: form.equipment, difficulty: form.difficulty,
        description: form.description, video_url: form.video_url, thumbnail: form.thumbnail,
        muscle_groups: form.muscleText.split(",").map((x) => x.trim()).filter(Boolean),
        instructions: instructions.length ? instructions : ["Perform with controlled form."],
        cues: lines(form.cuesText),
        common_mistakes: lines(form.mistakesText),
        safety_notes: form.safety_notes,
      };
      if (editId) { await api.put(`/exercises/${editId}`, payload); toast.success("Exercise updated!"); }
      else { await api.post("/exercises", payload); toast.success("Exercise added!"); }
      setShowNew(false); setEditId(null); setForm(blankForm); await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save exercise.");
    } finally { setSaving(false); }
  };

  const seedLibrary = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/exercises/seed-library");
      if (data.added > 0) toast.success(`Added ${data.added} exercises to your library.`);
      else toast.info("Your library already has all the starter exercises.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the starter library.");
    } finally { setSeeding(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/exercises/${confirmDel.id}`);
      toast.success("Exercise deleted.");
      setConfirmDel(null); setSelected(null); await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete exercise.");
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-5xl">Exercise Library</h1><p className="text-secondary mt-1">{exercises.length} movements</p></div>
        <div className="flex items-center gap-2">
          <button data-testid="ex-seed-library" onClick={seedLibrary} disabled={seeding} className="px-5 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:border-brand hover:text-brand disabled:opacity-60"><Download className="w-4 h-4" /> {seeding ? "Loading…" : "Load Starter Library"}</button>
          <button data-testid="ex-new" onClick={openNew} className="px-5 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover"><Plus className="w-4 h-4" /> New Exercise</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 text-tertiary absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="ex-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises" className="w-full bg-surface-elevated border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-brand" /></div>
        <div className="flex gap-1.5 overflow-x-auto">{CATS.map((c) => <button key={c} data-testid={`ex-cat-${c}`} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${cat === c ? "bg-brand text-white" : "card-surface text-secondary"}`}>{c}</button>)}</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
        {filtered.map((e) => (
          <button key={e.id} data-testid={`ex-card-${e.id}`} onClick={() => setSelected(e)} className="card-surface rounded-xl overflow-hidden text-left hover:border-brand transition-all">
            <div className="aspect-video bg-white/5 relative"><img src={e.thumbnail} alt="" className="w-full h-full object-cover" /><div className="absolute bottom-2 left-2"><Pill tone="danger">{e.category}</Pill></div></div>
            <div className="p-3"><p className="font-semibold truncate">{e.name}</p><p className="text-xs text-tertiary">{e.equipment} · {e.difficulty}</p></div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setSelected(null)}>
          <div className="card-surface rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative"><video src={selected.video_url} poster={selected.thumbnail} controls className="w-full aspect-video bg-black rounded-t-2xl" /><button data-testid="ex-modal-close" onClick={() => setSelected(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center"><X className="w-4 h-4" /></button></div>
            <div className="p-5">
              <h2 className="font-heading text-3xl">{selected.name}</h2>
              <div className="flex gap-1.5 mt-2 flex-wrap">{selected.muscle_groups?.map((m) => <Pill key={m}>{m}</Pill>)}<Pill>{selected.equipment}</Pill><Pill>{selected.difficulty}</Pill></div>
              <p className="text-secondary text-sm mt-3">{selected.description}</p>
              <p className="label-xs mt-4 mb-1">Instructions</p><ol className="text-sm text-secondary list-decimal list-inside space-y-1">{selected.instructions?.map((s, i) => <li key={i}>{s}</li>)}</ol>
              <p className="label-xs mt-4 mb-1">Coaching Cues</p><ul className="text-sm text-secondary list-disc list-inside">{selected.cues?.map((c, i) => <li key={i}>{c}</li>)}</ul>
              {selected.common_mistakes?.length > 0 && <><p className="label-xs mt-4 mb-1">Common Mistakes</p><ul className="text-sm text-secondary list-disc list-inside">{selected.common_mistakes.map((c, i) => <li key={i}>{c}</li>)}</ul></>}
              {selected.safety_notes && <div className="mt-4 bg-warning/10 border border-warning/20 rounded-lg p-3 text-xs text-warning">⚠ {selected.safety_notes}</div>}
              <div className="flex gap-2 mt-5">
                <button data-testid="ex-edit" onClick={() => openEdit(selected)} className="flex-1 py-2.5 border border-white/20 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 hover:border-white"><Pencil className="w-4 h-4" /> Edit</button>
                <button data-testid="ex-delete" onClick={() => setConfirmDel(selected)} className="flex-1 py-2.5 border border-brand/40 text-brand rounded-lg font-semibold text-sm flex items-center justify-center gap-2 hover:bg-brand/10"><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setShowNew(false)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">{editId ? "Edit Exercise" : "New Exercise"}</h2>
            <div className="space-y-3 mt-4">
              <input data-testid="ex-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Exercise name" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <div className="grid grid-cols-3 gap-2">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">{CATS.slice(1).map((c) => <option key={c}>{c}</option>)}</select>
                <select value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">{["Bodyweight", "Barbell", "Dumbbell", "Kettlebell", "Machine", "Bands"].map((c) => <option key={c}>{c}</option>)}</select>
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">{["Beginner", "Intermediate", "Advanced"].map((c) => <option key={c}>{c}</option>)}</select>
              </div>
              <div>
                <label className="label-xs block mb-1">Description</label>
                <textarea data-testid="ex-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short summary of the movement" rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="label-xs block mb-1">Muscle Groups <span className="text-tertiary normal-case">(comma separated)</span></label>
                <input data-testid="ex-form-muscles" value={form.muscleText} onChange={(e) => setForm({ ...form, muscleText: e.target.value })} placeholder="e.g. Quads, Glutes, Core" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="label-xs block mb-1">Instructions <span className="text-tertiary normal-case">(one step per line)</span></label>
                <textarea data-testid="ex-form-instructions" value={form.instructionsText} onChange={(e) => setForm({ ...form, instructionsText: e.target.value })} placeholder={"Set up in a braced position.\nLower under control...\nDrive back to the start."} rows={4} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="label-xs block mb-1">Coaching Cues <span className="text-tertiary normal-case">(one per line)</span></label>
                <textarea data-testid="ex-form-cues" value={form.cuesText} onChange={(e) => setForm({ ...form, cuesText: e.target.value })} placeholder={"Brace your core\nKeep a neutral spine"} rows={3} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="label-xs block mb-1">Common Mistakes / Alerts <span className="text-tertiary normal-case">(one per line)</span></label>
                <textarea data-testid="ex-form-mistakes" value={form.mistakesText} onChange={(e) => setForm({ ...form, mistakesText: e.target.value })} placeholder={"Rounding the lower back\nRushing the rep"} rows={3} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="label-xs block mb-1">Safety Notes</label>
                <textarea data-testid="ex-form-safety" value={form.safety_notes} onChange={(e) => setForm({ ...form, safety_notes: e.target.value })} placeholder="Any safety guidance for this movement" rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>


              <div>
                <label className="label-xs block mb-2">Cover Image</label>
                <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="ex-form-image-input" />
                {form.thumbnail ? (
                  <div className="flex items-center gap-3 bg-surface-elevated border border-border rounded-lg p-2">
                    <img src={form.thumbnail} alt="" className="w-20 h-14 rounded object-cover bg-black" />
                    <div className="flex-1 text-xs text-secondary">Cover image set</div>
                    <button type="button" data-testid="ex-form-image-change" onClick={() => imageRef.current?.click()} className="text-tertiary hover:text-white text-xs">Change</button>
                    <button type="button" data-testid="ex-form-image-remove" onClick={() => setForm({ ...form, thumbnail: null })} className="text-tertiary hover:text-brand text-xs">Remove</button>
                  </div>
                ) : (
                  <button type="button" data-testid="ex-form-image" onClick={() => imageRef.current?.click()}
                    className="w-full border border-dashed border-border rounded-lg py-4 flex flex-col items-center justify-center gap-1 hover:border-brand transition-colors">
                    <Upload className="w-5 h-5 text-tertiary" />
                    <span className="text-xs text-secondary">Upload cover image</span>
                    <span className="text-[10px] text-tertiary">Defaults to a category image if left empty</span>
                  </button>
                )}
              </div>

              <div>
                <label className="label-xs block mb-2">Demonstration Video</label>
                <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideo} data-testid="ex-form-video-input" />
                {form.video_url ? (
                  <div className="flex items-center gap-3 bg-surface-elevated border border-border rounded-lg p-2">
                    <video src={form.video_url} className="w-20 h-14 rounded object-cover bg-black" />
                    <div className="flex-1 text-xs text-secondary flex items-center gap-1"><Video className="w-4 h-4 text-success" /> Video attached</div>
                    <button type="button" onClick={() => setForm({ ...form, video_url: null })} className="text-tertiary hover:text-brand text-xs">Remove</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="ex-form-video" onClick={() => videoRef.current?.click()}
                      className="border border-dashed border-border rounded-lg py-4 flex flex-col items-center justify-center gap-1 hover:border-brand transition-colors">
                      <Upload className="w-5 h-5 text-tertiary" />
                      <span className="text-xs text-secondary">Upload clip</span>
                    </button>
                    <button type="button" data-testid="ex-form-record" onClick={() => setRecordingVid(true)}
                      className="border border-dashed border-border rounded-lg py-4 flex flex-col items-center justify-center gap-1 hover:border-brand transition-colors">
                      <Video className="w-5 h-5 text-tertiary" />
                      <span className="text-xs text-secondary">Record demo</span>
                    </button>
                  </div>
                )}
              </div>

              <button data-testid="ex-form-save" onClick={save} disabled={saving} className="w-full py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover disabled:opacity-60">{saving ? "Saving..." : editId ? "Save Changes" : "Add Exercise"}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-[60]" onClick={() => !deleting && setConfirmDel(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-brand/15 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-brand" /></div>
            <h2 className="font-heading text-3xl mt-3">Delete exercise?</h2>
            <p className="text-secondary text-sm mt-1">"{confirmDel.name}" will be permanently removed. This cannot be undone.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDel(null)} disabled={deleting} className="flex-1 py-2.5 border border-white/20 rounded-lg font-semibold text-sm hover:border-white">Cancel</button>
              <button data-testid="ex-delete-confirm" onClick={doDelete} disabled={deleting} className="flex-1 py-2.5 bg-brand text-white rounded-lg font-bold text-sm hover:bg-brand-hover disabled:opacity-60">{deleting ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
      {recordingVid && (
        <VideoRecorder label="Record Demo Video"
          onComplete={async (b64) => { try { const { data } = await api.post("/upload/media", { data: b64, kind: "video" }); setForm((f) => ({ ...f, video_url: data.url })); setRecordingVid(false); toast.success("Demo recorded & attached."); } catch (err) { toast.error("Upload failed, please try again."); } }}
          onClose={() => setRecordingVid(false)} />
      )}
    </div>
  );
}
